import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Award, Target, Star, RefreshCw } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import achievementService from '../../services/achievementService';
import { getFullImageUrl } from '../../utils/imageUpload';
import toast from 'react-hot-toast';
import AchievementModal from './AchievementModal';

const AchievementsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const AchievementCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 25px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ffd700 0%, #ffed4e 100%);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  }
`;

const AchievementHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const AchievementIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #1a1a2e;
  box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
`;

const AchievementInfo = styled.div`
  flex: 1;
`;

const AchievementName = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px 0;
`;

const AchievementPoints = styled.div`
  color: #ffd700;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const AchievementDescription = styled.p`
  color: #b8b8d1;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const AchievementTrigger = styled.div`
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const TriggerTitle = styled.div`
  color: #00d4ff;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TriggerDescription = styled.div`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
`;

const AchievementActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const response = await achievementService.getAchievements();
      setAchievements(response || []);
    } catch (error) {
      toast.error('خطا در بارگذاری دستاوردها');
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAchievements = achievements.filter(achievement =>
    achievement.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    achievement.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddAchievement = () => {
    setSelectedAchievement(null);
    setIsModalOpen(true);
  };

  const handleEditAchievement = (achievement) => {
    setSelectedAchievement(achievement);
    setIsModalOpen(true);
  };

  const handleSaveAchievement = async (formData) => {
    try {
      if (selectedAchievement) {
        // Update existing achievement
        await achievementService.updateAchievement(selectedAchievement.id, formData);
        toast.success('دستاورد با موفقیت ویرایش شد');
      } else {
        // Create new achievement
        await achievementService.createAchievement(formData);
        toast.success('دستاورد با موفقیت ایجاد شد');
      }
      setIsModalOpen(false);
      fetchAchievements();
    } catch (error) {
      toast.error('خطا در ذخیره دستاورد');
      console.error('Error saving achievement:', error);
    }
  };

  const handleRefreshAchievements = async () => {
    try {
      setLoading(true);
      await fetchAchievements();
      toast.success('دستاوردها با موفقیت به‌روزرسانی شدند');
    } catch (error) {
      toast.error('خطا در بازیابی دستاوردها'); 
    } finally {
      setLoading(false);
    }
  };

  const getTriggerText = (triggerType, threshold) => {
    const triggerTexts = {
      'TotalKills': 'کل کشته‌ها',
      'TotalWins': 'کل بردها',
      'TotalMatchesPlayed': 'کل مسابقات انجام شده',
      'ReachSpecificRank': 'رسیدن به رتبه خاص',
      'KillsInASingleMatch': 'کشته در یک مسابقه',
      'ConsecutiveLosses': 'باخت متوالی',
      'ConsecutiveDailyPlay': 'بازی روزانه متوالی'
    };
    
    const text = triggerTexts[triggerType] || triggerType;
    return `${text} >= ${threshold}`;
  };

  if (loading) {
    return (
      <AchievementsContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری دستاوردها...</div>
        </div>
      </AchievementsContainer>
    );
  }

  return (
    <AchievementsContainer>
      <Header>
        <Title>مدیریت دستاوردها</Title>
        <SearchContainer>
          <Input
            placeholder="جستجو در دستاوردها..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Button 
            variant="outline"
            icon={<RefreshCw size={16} />}
            onClick={handleRefreshAchievements}
            disabled={loading}
          >
            بازیابی
          </Button>
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" onClick={handleAddAchievement} icon={<Plus size={16} />}>
            افزودن دستاورد
          </Button>
        </SearchContainer>
      </Header>

      {filteredAchievements.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🏆</EmptyIcon>
          <EmptyTitle>هیچ دستاوردی یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز دستاوردی ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <AchievementsGrid>
          {filteredAchievements.map((achievement, index) => (
            <AchievementCard
              key={achievement.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <AchievementHeader>
                <AchievementIcon>
                  {achievement.iconUrl ? (
                    <img 
                      src={getFullImageUrl(achievement.iconUrl)} 
                      alt={achievement.name}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Award size={24} />
                  )}
                </AchievementIcon>
                <AchievementInfo>
                  <AchievementName>{achievement.name || 'دستاورد بدون نام'}</AchievementName>
                  {achievement.points && (
                    <AchievementPoints>
                      <Star size={16} />
                      {achievement.points} امتیاز
                    </AchievementPoints>
                  )}
                </AchievementInfo>
              </AchievementHeader>

              <AchievementDescription>
                {achievement.description || 'توضیحاتی برای این دستاورد وجود ندارد'}
              </AchievementDescription>

              <AchievementTrigger>
                <TriggerTitle>شرط دستیابی</TriggerTitle>
                <TriggerDescription>
                  {getTriggerText(achievement.triggerType, achievement.triggerThreshold)}
                </TriggerDescription>
              </AchievementTrigger>

              <AchievementActions>
                <ActionButton onClick={() => handleEditAchievement(achievement)} title="ویرایش">
                  <Edit size={16} />
                </ActionButton>
              </AchievementActions>
            </AchievementCard>
          ))}
        </AchievementsGrid>
      )}

      <AchievementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        achievement={selectedAchievement}
        onSave={handleSaveAchievement}
      />
    </AchievementsContainer>
  );
};

export default Achievements;
